import { animate, style, transition, trigger } from "@angular/animations";
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { map } from "rxjs/operators";
import { DataService } from "src/app/services/Data.service";
import { UserService } from "src/app/services/User.service";
import { NAVIGATIONS } from "src/app/shared/consts/navigations";
import { AdapterService } from "../adapters/chatbot-adapter.service";
import { ChatbotDisplayer } from "../adapters/chatbot-displayer";
import { ChatbotManage } from "../adapters/chatbot-manage";
import { Conversation, Message } from "../domain/models/chatbot";
import Swal from "sweetalert2";
import { Subject } from "rxjs";
import { ShareConversationModalComponent } from "src/app/shared/components/share-conversation-modal/share-conversation-modal.component";
import { debounceTime } from "rxjs/operators";

@Component({
  selector: "app-chatbot",
  templateUrl: "./chatbot.component.html",
  styleUrls: ["./chatbot.component.css"],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("600ms", style({ opacity: 0.85 })),
      ]),
      transition(":leave", [animate("600ms", style({ opacity: 0 }))]),
    ]),
    trigger("messageAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(20px)" }),
        animate(
          "400ms cubic-bezier(.35,0,.25,1)",
          style({ opacity: 1, transform: "none" })
        ),
      ]),
    ]),
  ],
  providers: [ChatbotDisplayer, ChatbotManage, AdapterService],
})
export class ChatbotComponent implements OnInit, AfterViewInit {
  userId: number;
  loading: boolean = false;
  sidebarOpen = false;
  darkMode = false;
  selectedConversationId: string | null = null;
  historyAtTop = true;
  historyAtBottom = false;
  @ViewChild("messagesContainer")
  messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild("historyList") historyList!: ElementRef<HTMLUListElement>;
  @ViewChild("shareModal") shareModal!: ShareConversationModalComponent;

  conversations$ = this.display.conversations$;
  messages: any[] = [];
  messages$ = this.display.messages$.pipe(
    map((messages) => {
      this.messages = messages;
      return messages.map((msg) => ({
        ...msg,
        safeContent: this.sanitizer.bypassSecurityTrustHtml(msg.content),
      }))
    }
    )
  );
  loading$ = this.display.loading$;
  userInput: string = "";
  enlargedImgUrl: string | null = null;
  readonly NAVIGATIONS = NAVIGATIONS;

  stopRequested = false;
  filterImages: boolean = true;
  filterResponseType: "simple" | "advanced" | "technical" = "simple";

  showPendingFunctionalities: boolean = false;

  editMessageIndex: number | null = null;
  editMessageText: string = "";

  pageNumber = 1;
  pageSize = 50;
  useCache = false;
  loadingConversations = false;
  hasMoreConversations = true;

  menuPosition = { top: 0, left: 0 };
  openConversationMenuIndex: number | null = null;

  historySearch: string = "";
  private historySearchSubject = new Subject<string>();


  constructor(
    private display: ChatbotDisplayer,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.dataService.currentUserIdSource.subscribe(
      (userId) => {
        this.userId = userId;
        if (userId != 0)
          this.loadConversations(userId);
      }
    );

    this.applyThemeFromStorage();

    this.display.activeConversationId$.subscribe((id) => {
      this.selectedConversationId = id;
    });

    this.display.scrollTrigger$.subscribe(() => {
      this.scrollToBottom();
    });

    this.historySearchSubject.pipe(debounceTime(400)).subscribe(search => {
      this.pageNumber = 1;
      this.hasMoreConversations = true;
      this.loadConversations(this.userId, true, search);
    });

    this.scrollToTop();
  }

  ngAfterViewInit() {
    this.messagesContainer.nativeElement.addEventListener(
      "click",
      (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains("file-attachment")) {
          const fileName = target.getAttribute("data-file");
          if (fileName) {
            this.downloadDocument(fileName);
          }
        } else if (target.tagName === "IMG") {
          this.enlargedImgUrl = (target as HTMLImageElement).src;
        }
      }
    );

    this.historyList.nativeElement.addEventListener("scroll", () => {
      const el = this.historyList.nativeElement;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
        this.loadConversations(this.userId);
      }
    });

    this.conversations$.subscribe(() => {
      this.tryAutoLoadConversations();
    });

    this.messages$.subscribe(() => this.checkShowScrollToBottom());
  }

  onHistorySearchChange(value: string) {
    this.historySearchSubject.next(value);
  }

  private tryAutoLoadConversations() {
    setTimeout(() => {
      const el = this.historyList?.nativeElement;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight && this.hasMoreConversations && !this.loadingConversations) {
        this.loadConversations(this.userId);
      }
    });
  }

  private applyThemeFromStorage() {
    const dark = localStorage.getItem("darkMode");
    this.darkMode = dark === "true";
    if (this.darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }

  async loadConversations(userId: number, reset: boolean = false, search: string = "") {
    if (this.loadingConversations || !this.hasMoreConversations) return;
    this.loadingConversations = true;
    if (reset) {
      this.pageNumber = 1;
      this.hasMoreConversations = true;
    }
    try {
      await this.display.getConversations(userId, this.pageNumber, this.pageSize, this.useCache, search);
      let loaded = 0;
      this.display.conversations$.pipe(map(convs => convs.length)).subscribe(count => loaded = count).unsubscribe();
      if (loaded < this.pageNumber * this.pageSize) {
        this.hasMoreConversations = false;
      } else {
        this.pageNumber++;
      }
    } catch (error) {
      console.error("Error loading conversations", error);
    }
    this.loadingConversations = false;
  }

  async downloadDocument(documentName: string): Promise<void> {
    if (!documentName) return;
    await this.display.downloadDocument(documentName);
  }

  closeImgViewer() {
    this.enlargedImgUrl = null;
  }

  async sendMessage(): Promise<void> {
    const message = this.userInput.trim();
    if (!message) return;
    this.userInput = "";
    this.stopRequested = false;

    const textarea = document.querySelector('#user-message-input') as HTMLTextAreaElement;
    textarea.style.height = 'auto';

    await this.display.sendMessage(
      message,
      this.userId,
      this.darkMode ? 'D' : 'L',
      'Original',
      () => this.stopRequested
    );

    this.userService.incrementQueryCount().subscribe();
  }

  stopResponse(): void {
    this.stopRequested = true;
    this.display.forceStopResponse();
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  selectConversation(conversation: Conversation): void {
    this.display.selectConversation(conversation.id);
  }

  showScrollToBottom = false;

  scrollToBottom(): void {
    setTimeout(() => {
      const messagesEl = this.messagesContainer?.nativeElement;
      if (messagesEl && messagesEl.lastElementChild) {
        messagesEl.lastElementChild.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    });
  }

  checkShowScrollToBottom() {
    setTimeout(() => {
      const messagesEl = this.messagesContainer?.nativeElement;
      if (!messagesEl) return;
      this.showScrollToBottom = messagesEl.scrollHeight > window.innerHeight;
    });
  }

  scrollToTop(): void {
    setTimeout(() => {
      const messagesEl = this.messagesContainer?.nativeElement;
      if (messagesEl && messagesEl.lastElementChild) {
        messagesEl.lastElementChild.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });
  }

  copyMessage(message: Message) {
    this.display.copyMessage(message);
  }

  trackById(index: number, msg: Message) {
    return msg.id;
  }

  likeMessage(message: Message) {
    this.display.likeMessage(this.userId, message);
  }


  dislikeMessage(message: Message) {
    this.openCommentsSuggestions(message);
  }

  createNewChat() {
    this.display.createNewChat();
  }

  listenMessage(message: Message) {
    this.display.listenMessage(message);
  }

  tryAgain(lastIndex: number) {
    const lastUserMessageIndex = lastIndex - 1;
    if (this.messages[lastUserMessageIndex].role === 'user') {
      this.stopRequested = false;
      this.display.sendMessage(this.messages[lastUserMessageIndex].content, this.userId, this.darkMode ? 'D' : 'L', 'Retry', () => this.stopRequested);
    }
  }

  isLastMessage(role: string, index: number, messages: Message[]): boolean {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === role) {
        return i === index;
      }
    }
    return false;
  }

  editLastUserMessage(lastUserMessageIndex: number) {
    this.editMessageIndex = lastUserMessageIndex;
    this.editMessageText = this.messages[lastUserMessageIndex].content;

    setTimeout(() => {
      const input = document.querySelector('.edit-message-input') as HTMLInputElement;
      if (input) {
        const textarea = document.querySelector('#message-edit-input') as HTMLTextAreaElement;
        if (textarea.value.trim() === '') {
          textarea.style.height = 'auto';
        } else {
          textarea.style.height = textarea.scrollHeight + 'px';
        }
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    });
  }

  acceptEditMessage() {
    if (this.editMessageIndex === null) return;
    const userIdx = this.editMessageIndex;
    const botIdx = userIdx + 1;
    const editedText = this.editMessageText;

    this.editMessageIndex = null;
    this.editMessageText = "";

    const textarea = document.querySelector('#message-edit-input') as HTMLTextAreaElement;
    textarea.style.height = 'auto';

    this.display.removeMessages([userIdx, botIdx]);

    this.stopRequested = false;
    this.display.sendMessage(
      editedText,
      this.userId,
      this.darkMode ? 'D' : 'L',
      'Edit',
      () => this.stopRequested
    );
  }

  cancelEditMessage() {
    this.editMessageIndex = null;
    this.editMessageText = "";
  }

  toggleTheme() {
    this.darkMode = !this.darkMode;
    const body = document.body;
    if (this.darkMode) {
      body.classList.add("dark-mode");
    } else {
      body.classList.remove("dark-mode");
    }
    localStorage.setItem("darkMode", this.darkMode ? "true" : "false");
  }

  setFilterImages(value: boolean) {
    this.filterImages = value;
    this.saveFiltersToStorage();
  }

  setFilterResponseType(value: "simple" | "advanced" | "technical") {
    this.filterResponseType = value;
    this.saveFiltersToStorage();
  }

  toggleConversationMenu(idx: number, event: MouseEvent) {
    event.stopPropagation();
    let button = event.target as HTMLElement;

    if (button.tagName === 'SPAN' && button.parentElement) {
      button = button.parentElement;
    }

    const rect = button.getBoundingClientRect();
    const menuHeight = 200;
    const viewportHeight = window.innerHeight;

    let top = rect.top + rect.height + 4;

    if (rect.bottom + menuHeight > viewportHeight) {
      top = rect.top - menuHeight - 4;
    }

    this.menuPosition = { top, left: rect.left - 104 };
    this.openConversationMenuIndex = idx;
  }

  renameConversation(conversation: Conversation) {
    Swal.fire({
      title: 'Rename Conversation',
      input: 'text',
      inputValue: conversation.title,
      showCancelButton: true,
      confirmButtonText: 'Rename',
      cancelButtonText: 'Cancel',
      confirmButtonColor: "#009",
      cancelButtonColor: "#aaa",
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please enter a new name';
        }
        return null;
      }
    }).then((result) => {
      if (result.isConfirmed && result.value && result.value.trim().length > 0) {
        this.display.updateConversation(conversation.id, this.userId, result.value.trim(), false, false, 'renaming');
      }
      this.openConversationMenuIndex = null;
    });
  }

  pinConversation(conversation: Conversation) {
    this.display.updateConversation(
      conversation.id,
      this.userId,
      conversation.title,
      !conversation.pinned,
      false,
      'pinning'
    );
    this.openConversationMenuIndex = null;
  }

  deleteConversation(conversation: Conversation) {
    Swal.fire({
      title: 'Delete Conversation',
      text: `Are you sure you want to delete "${conversation.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: "#990000",
      cancelButtonColor: "#aaa",
    }).then((result) => {
      if (result.isConfirmed) {
        this.display.updateConversation(conversation.id, this.userId, conversation.title, false, true, 'deleting');
      }
      this.openConversationMenuIndex = null;
    });
  }

  downloadConversationPdf(conversationId: string): void {
    if (!conversationId) return;
    this.display.exportConversationToPdf(conversationId);
    this.openConversationMenuIndex = null;
  }

  shareConversationPdf(conversationId: string): void {
    if (!conversationId) return;
    
    // Find the conversation to get its title
    this.display.conversations$.subscribe(conversations => {
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation) {
        this.shareModal.open({
          conversationId: conversationId,
          conversationTitle: conversation.title
        });
      }
    }).unsubscribe();
    
    this.openConversationMenuIndex = null;
  }

  handleShareConversation(shareData: { conversationId: string, emails: string[], mailContent: string, subject: string }): void {
    this.display.shareConversation(shareData.conversationId, shareData.emails, shareData.mailContent, shareData.subject)
      .then(() => {
        this.shareModal.onShareCompleted();
        Swal.fire({
          title: 'Success!',
          text: `Conversation shared successfully to ${shareData.emails.length} recipient(s).`,
          icon: 'success',
          confirmButtonColor: "#009"
        });
      })
      .catch((error) => {
        this.shareModal.onShareError();
        Swal.fire({
          title: 'Error',
          text: 'Failed to share conversation. Please try again.',
          icon: 'error',
          confirmButtonColor: "#009"
        });
        console.error('Error sharing conversation:', error);
      });
  }

  clearHistorySearch() {
    this.historySearch = "";
    this.onHistorySearchChange("");
  }
  @HostListener('document:click')
  closeMenus() {
    this.openConversationMenuIndex = null;
  }

  openCommentsSuggestions(message?: Message) {
    const isDislike = !!message;
    const title = isDislike ? 'Your opinion helps us improve' : 'Comments and suggestions';
    const placeholder = isDislike ? 'Please describe what was wrong... (Optional)' : 'Please share your comments or suggestions about MS AI-Agent...';
    const confirmButtonText = isDislike ? 'Dislike' : 'Submit';
    Swal.fire({
      title,
      html: `
        <div style="text-align: center;">          
          <textarea id="swal-comments" class="swal2-textarea" maxlength="250" placeholder="${placeholder}" style="margin: 1rem 5px 5px 5px;display:block;width:calc(100% - 10px);resize:vertical;"></textarea>
          <div id="swal-char-count" style="text-align:right;margin-right:5px;font-size:12px;color:#888;">0/250</div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText: 'Cancel',
      confirmButtonColor: isDislike ? "#990000" :"#009",
      cancelButtonColor: "#aaa",
      preConfirm: () => {
        const comments = (document.getElementById('swal-comments') as HTMLTextAreaElement).value;
        if (!isDislike && (!comments || comments.trim() === '')) {
          Swal.showValidationMessage('Please enter your comments');
          return false;
        }
        if (comments && comments.length > 250) {
          Swal.showValidationMessage("Comments must be 250 characters or less");
          return false;
        }
        return { comment: comments.trim() };
      },
      didOpen: () => {
        const textarea = document.getElementById('swal-comments') as HTMLTextAreaElement;
        const counter = document.getElementById('swal-char-count');
        textarea.addEventListener('input', () => {
          counter.textContent = `${textarea.value.length}/250`;
        });
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        const { comment } = result.value;
        if (isDislike && message) {
          this.display.dislikeMessage(this.userId, message, comment);
        } else {
          await this.display.submitComment(this.userId, comment);
        }
      }
    });
  }

  private saveFiltersToStorage() {
    localStorage.setItem("filterImages", this.filterImages ? "true" : "false");
    localStorage.setItem("filterResponseType", this.filterResponseType);
  }

  autoExpand(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    if (textarea.value.trim() === '') {
      textarea.style.height = 'auto';
    } else {
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }
}
